/**
 * AddFoodDialogWithSelector — top-level Add Food dialog that exposes a meal-time
 * selector (used from the page header, not from inside a meal group).
 */
import * as React from "react";

import {
  Widget,
  WidgetContent,
  WidgetHeader,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  useAddMealEntry,
  useFoodSearch,
  useFoodServings,
} from "@/hooks/useNutritionEvaluation";
import { cn } from "@/lib/utils";
import type { MealTime } from "@/types/nutrition";
import { Apple, Loader2 } from "lucide-react";

import { MEAL_TIME_DISPLAY, quantityPatternFor } from "./meal-constants";
import { FoodNutrientPieChart } from "./nutrition-charts";
import { NUTRITION_COLORS } from "./nutrition-chart-config";

interface AddFoodDialogWithSelectorProps {
  open: boolean;
  onClose: () => void;
  mealTimes: MealTime[];
  appointmentId: string;
  isTubeFeeding: boolean;
  isMealPlan?: boolean;
  planIndex?: number;
}

export const AddFoodDialogWithSelector: React.FC<
  AddFoodDialogWithSelectorProps
> = ({
  open,
  onClose,
  mealTimes,
  appointmentId,
  isTubeFeeding,
  isMealPlan = false,
  planIndex: _planIndex,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedFood, setSelectedFood] = React.useState<{
    id: number;
    name: string;
    energy_kcal: string;
    food_item_nutrient_values?: {
      energy: number;
      protein: number;
      carbs: number;
      fat: number;
      fiber: number;
    };
  } | null>(null);
  const [selectedServing, setSelectedServing] = React.useState<string>("");
  const [quantity, setQuantity] = React.useState("1");
  const [selectedMealGroupId, setSelectedMealGroupId] = React.useState<string>(
    mealTimes[0]?.id || ""
  );

  const { data: searchResults, isLoading: isSearching } = useFoodSearch(
    searchQuery,
    appointmentId,
    searchQuery.length >= 2
  );

  const { data: servings = [] } = useFoodServings(
    selectedFood?.id || 0,
    !!selectedFood
  );

  const addEntry = useAddMealEntry(appointmentId, isTubeFeeding, isMealPlan);

  const getAdjustedNutrients = React.useCallback(() => {
    if (!selectedFood?.food_item_nutrient_values) return null;
    const serving = servings.find((s) => s.id.toString() === selectedServing);
    const multiplier = serving ? parseFloat(serving.multiplier) : 1;
    const qty = parseFloat(quantity) || 0;
    const totalMultiplier = qty * multiplier;

    const nutrients = selectedFood.food_item_nutrient_values;
    return {
      energy: nutrients.energy * totalMultiplier,
      protein: nutrients.protein * totalMultiplier,
      carbs: nutrients.carbs * totalMultiplier,
      fat: nutrients.fat * totalMultiplier,
      fiber: nutrients.fiber * totalMultiplier,
    };
  }, [selectedFood, selectedServing, quantity, servings]);

  const adjustedNutrients = getAdjustedNutrients();

  const resetForm = () => {
    setSearchQuery("");
    setSelectedFood(null);
    setSelectedServing("");
    setQuantity("1");
  };

  // Whole servings only when the food contains something indivisible — a
  // capsule or a strip. Keyed on the food's flag, never on "is a supplement":
  // a whey scoop halves perfectly well.
  const wholeServingsOnly = Boolean(
    (selectedFood as { requires_whole_servings?: boolean } | null)
      ?.requires_whole_servings
  );

  const handleQuantityChange = (value: string) => {
    if (!quantityPatternFor(wholeServingsOnly).test(value)) return;
    setQuantity(value);
  };

  const handleAdd = () => {
    if (selectedFood && selectedServing && quantity && selectedMealGroupId) {
      addEntry.mutate({
        meal_group: selectedMealGroupId,
        food_item: selectedFood.id,
        serving_size: parseInt(selectedServing),
        quantity,
      } as Parameters<typeof addEntry.mutate>[0]);
      onClose();
      resetForm();
    }
  };

  React.useEffect(() => {
    if (mealTimes.length > 0 && !selectedMealGroupId) {
      setSelectedMealGroupId(mealTimes[0].id);
    }
  }, [mealTimes, selectedMealGroupId]);

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        resetForm();
      }}
    >
      <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <Apple className="h-5 w-5 text-primary" />
            Add Food Item
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="flex-1 overflow-y-auto p-6 bg-background flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <span className="z-h3 text-foreground">Food Group:</span>
            <Select
              value={selectedMealGroupId}
              onValueChange={setSelectedMealGroupId}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select meal time" />
              </SelectTrigger>
              <SelectContent>
                {mealTimes.map((mealTime) => (
                  <SelectItem key={mealTime.id} value={mealTime.id}>
                    {MEAL_TIME_DISPLAY[mealTime.meal_time] ||
                      mealTime.meal_time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Input
            placeholder="Start typing food item..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedFood(null);
            }}
          />

          <div className="flex-1 overflow-hidden grid grid-cols-2 gap-4">
            <Widget className="overflow-hidden">
              <WidgetHeader
                title={
                  isSearching
                    ? "Searching..."
                    : `Food Items (${searchResults?.results?.length || 0})`
                }
              />
              <ScrollArea className="h-64">
                <WidgetContent className="px-2 pb-2">
                  {isSearching && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  )}

                  {searchResults?.results &&
                    searchResults.results.length > 0 && (
                      <div className="space-y-1">
                        {searchResults.results.map((food) => (
                          <button
                            key={food.id}
                            className={cn(
                              "w-full px-3 py-2 text-left rounded-md transition-colors",
                              selectedFood?.id === food.id
                                ? "bg-primary text-primary-foreground"
                                : "hover:bg-muted"
                            )}
                            onClick={() => {
                              setSelectedFood(food);
                              setSelectedServing("");
                            }}
                          >
                            <div className="z-body-md">{food.name}</div>
                            {/* Source alongside energy: two foods can share a
                                name (IFCT's "Almond" and ours), and the wrong
                                pick lands silently in a patient's recall. Plain
                                text, not a Badge — this row inverts to
                                primary-foreground when selected. */}
                            <div className="z-caption opacity-75">
                              {[
                                food.source?.trim(),
                                `${food.energy_kcal} kcal/100g`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                  {searchQuery.length >= 2 &&
                    !isSearching &&
                    searchResults?.results?.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No food items found
                      </div>
                    )}

                  {searchQuery.length < 2 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Type at least 2 characters to search
                    </div>
                  )}
                </WidgetContent>
              </ScrollArea>
            </Widget>

            <Widget className="overflow-hidden">
              <WidgetHeader
                title={selectedFood ? selectedFood.name : "Select a food item"}
              />
              <WidgetContent className="space-y-4">
                {selectedFood && (
                  <>
                    {adjustedNutrients && (
                      <div className="flex items-start gap-4">
                        <FoodNutrientPieChart
                          energy={adjustedNutrients.energy}
                          protein={adjustedNutrients.protein}
                          carbs={adjustedNutrients.carbs}
                          fat={adjustedNutrients.fat}
                          fiber={adjustedNutrients.fiber}
                        />
                        <div className="flex flex-col gap-1 z-body flex-1">
                          {(
                            [
                              { key: "carbs", label: "Carbs", k: 4 },
                              { key: "protein", label: "Protein", k: 4 },
                              { key: "fiber", label: "Fiber", k: 2 },
                              { key: "fat", label: "Fats", k: 9 },
                            ] as const
                          ).map((m) => (
                            <div
                              key={m.key}
                              className="flex items-center gap-2"
                            >
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                  backgroundColor:
                                    NUTRITION_COLORS[
                                      m.key === "fat" ? "fats" : m.key
                                    ],
                                }}
                              />
                              <span>
                                {m.label}: {adjustedNutrients[m.key].toFixed(1)}
                                g
                              </span>
                              <span className="text-muted-foreground">
                                (
                                {adjustedNutrients.energy > 0
                                  ? (
                                      ((adjustedNutrients[m.key] * m.k) /
                                        adjustedNutrients.energy) *
                                      100
                                    ).toFixed(1)
                                  : 0}
                                %)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <Separator />

                    {servings.length > 0 && (
                      <div className="space-y-2">
                        <label className="z-h3 text-foreground">
                          Serving Size
                        </label>
                        <Select
                          value={selectedServing}
                          onValueChange={setSelectedServing}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select serving size" />
                          </SelectTrigger>
                          <SelectContent>
                            {servings.map((serving) => (
                              <SelectItem
                                key={serving.id}
                                value={serving.id.toString()}
                              >
                                {serving.description ||
                                  serving.serving_unit.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {selectedServing && (
                      <div className="space-y-2">
                        <label className="z-h3 text-foreground">Quantity</label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={quantity}
                          onChange={(e) => handleQuantityChange(e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}
              </WidgetContent>
            </Widget>
          </div>
        </div>

        <DialogFooter className="p-6 mt-auto border-t border-border/60 bg-background flex justify-end gap-2">
          <Button
            onClick={handleAdd}
            disabled={
              !selectedFood ||
              !selectedServing ||
              !quantity ||
              !selectedMealGroupId ||
              addEntry.isPending
            }
          >
            {addEntry.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Food Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
